import React from 'react';
import FilterListIcon from '@mui/icons-material/FilterList';

import { RadioGroup, Radio, styled } from '@mui/material';
import { Accordion, Checkbox, Colors, OutlinedDropdown, Text } from 'library';
import Label from 'reusableComponents/checkboxLabel';

import { useUserContext } from 'UserContext';
import { STAFF, WORKED } from '../constants';

import type { FilterProps } from '../models';

export function Filters({
    formValues,
    handleChange,
}: {
    formValues: FilterProps;
    handleChange: (value: string | boolean, formKey: string) => void;
}) {
    const { user } = useUserContext();

    const addresses = user?.address || [];

    const sortOptions = [
        { value: 'Most recently worked', key: 'last_shift' },
        { value: 'Name (A–Z)', key: 'name_asc' },
        { value: 'Name (Z–A)', key: 'name_desc' },
    ];

    const selectOptions = [
        { value: 'All Locations', key: '-1' },
        ...addresses.map((x) => {
            return {
                value: x.business_location?.name
                    ? `${x.business_location.name} — ${x.city}, ${x.state}`
                    : `${x.street} ${x.city}, ${x.state} ${x.zip_code}`,
                key: x.id.toString(),
            };
        }),
    ];

    const title = (
        <TitleRow>
            <FilterListIcon style={{ color: Colors.mediumNavy, fontSize: 20, marginRight: 8 }} />
            <Text bold variant="body1">
                Filters
            </Text>
        </TitleRow>
    );

    return (
        <FilterContainer>
            <Accordion title={title} defaultExpanded sx={accordionSx}>
                <FilterSection>
                    <SectionLabel>
                        <Text
                            variant="caption"
                            bold
                            textStyle={{ textTransform: 'uppercase', letterSpacing: 0.5, color: Colors.mediumNavy }}
                        >
                            Location
                        </Text>
                    </SectionLabel>
                    <OutlinedDropdown
                        value={formValues.address}
                        fields={selectOptions}
                        onChange={(x) => handleChange(x.target.value, 'address')}
                        fullWidth
                        style={dropdownStyle}
                    />
                </FilterSection>
                <FilterSection>
                    <SectionLabel>
                        <Text
                            variant="caption"
                            bold
                            textStyle={{ textTransform: 'uppercase', letterSpacing: 0.5, color: Colors.mediumNavy }}
                        >
                            Workers
                        </Text>
                    </SectionLabel>
                    <RadioGroup
                        value={formValues.workerType}
                        onChange={(event: React.ChangeEvent<HTMLInputElement>) =>
                            handleChange(event.target.value, 'workerType')
                        }
                    >
                        <Label
                            control={<Radio color="primary" size="small" disableRipple disableFocusRipple />}
                            labelPlacement="end"
                            value={WORKED}
                            label={<Text variant="body2">Worked at my center</Text>}
                        />
                        <Label
                            control={<Radio color="primary" size="small" disableRipple disableFocusRipple />}
                            labelPlacement="end"
                            value={STAFF}
                            label={<Text variant="body2">Scheduled at my center</Text>}
                        />
                    </RadioGroup>
                </FilterSection>
                <FilterSection>
                    <SectionLabel>
                        <Text
                            variant="caption"
                            bold
                            textStyle={{ textTransform: 'uppercase', letterSpacing: 0.5, color: Colors.mediumNavy }}
                        >
                            Sort by
                        </Text>
                    </SectionLabel>
                    <OutlinedDropdown
                        value={formValues.sort}
                        fields={sortOptions}
                        onChange={(x) => handleChange(x.target.value, 'sort')}
                        fullWidth
                        style={dropdownStyle}
                    />
                </FilterSection>
                <FilterSection>
                    <SectionLabel>
                        <Text
                            variant="caption"
                            bold
                            textStyle={{ textTransform: 'uppercase', letterSpacing: 0.5, color: Colors.mediumNavy }}
                        >
                            Show
                        </Text>
                    </SectionLabel>
                    <CheckboxRow>
                        <Checkbox
                            label="Preferred only"
                            checked={formValues.showPreferred}
                            onChange={(event: boolean) => handleChange(event, 'showPreferred')}
                        />
                    </CheckboxRow>
                </FilterSection>
            </Accordion>
        </FilterContainer>
    );
}

const accordionSx = {
    width: 260,
    backgroundColor: Colors.white,
    borderRadius: '12px !important',
    padding: '8px 16px',
    boxShadow: '0px 2px 8px rgba(0, 0, 0, 0.08)',
    '&:before': { display: 'none' },
};

const FilterContainer = styled('div')({
    width: 260,
    flexShrink: 0,
    height: 'fit-content',
});

const TitleRow = styled('div')({
    display: 'flex',
    alignItems: 'center',
});

const FilterSection = styled('div')({
    marginTop: 16,
});

const SectionLabel = styled('div')({
    marginBottom: 6,
});

const CheckboxRow = styled('div')({
    marginTop: 4,
});

const dropdownStyle: React.CSSProperties = {
    borderRadius: 8,
    height: 36,
    fontSize: 13,
    width: '100%',
};
